import {
  UICorePlugin,
  template,
  type TemplateStringifier,
  type SupportedVersion,
  $,
  type FormattedError
} from '@clappr/core'
import {Events} from 'common/types/events'
import type {Options} from 'common/types/options'
import type {MediaControl} from 'common/plugins/media-control'
import type {Advertisement} from 'common/plugins/advertisement'
import * as skins from './skins'

export class TimeToAdvertisement<T extends Options> extends UICorePlugin<T> {
  private static SHOW_TIMEOUT = 5
  private static template = ''

  public static async preload(options: Options): Promise<void> {
    if (TimeToAdvertisement.template) return

    const skinName = options.mediaControl?.skinName
    const loadSkin = (skinName && (skins as any)[skinName]) || skins.rambler

    if (typeof loadSkin === 'function') {
      const {default: template} = await loadSkin()
      if (template) TimeToAdvertisement.template = template
    }
  }

  private isActive = false

  public get name(): string {
    return 'timeToAdvertisement'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  private get template(): TemplateStringifier {
    return template(TimeToAdvertisement.template)
  }

  private get advertisement(): Advertisement<T> | null {
    return this.core.getPlugin('advertisement') as Advertisement<T> | null
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  public get isVisible(): boolean {
    return this.$el.is(':visible')
  }

  public bindEvents(): void {
    const {activeContainer} = this.core

    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    if (activeContainer) {
      this.listenTo(activeContainer, Events.CONTAINER_PLAY, () => {
        if (this.isActive) this.show()
      })
      this.listenTo(activeContainer, Events.CONTAINER_PAUSE, () => {
        this.hide()
      })
    }

    if (this.advertisement) {
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_TIME_TO_PREROLL,
        this.onTimeToPreroll
      )

      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_ERROR,
        this.onAdError
      )
    }
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  private onTimeToPreroll(remainTimeToAd: number): void {
    const {activeContainer} = this.core

    if (
      remainTimeToAd <= TimeToAdvertisement.SHOW_TIMEOUT &&
      remainTimeToAd > 0
    ) {
      if (activeContainer.isPlaying()) {
        this.isActive = true
        this.$el.find('span').html(String(Math.ceil(remainTimeToAd)))

        if (activeContainer.getCurrentTime()) this.show()
      }
    } else if (remainTimeToAd <= 0) {
      this.deactivate()
    }
  }

  private onAdError(error: FormattedError): void {
    const {advertisement} = this
    const blockType = error.raw?.blockType

    if (blockType && advertisement?.isPreRollWithStartTime(blockType)) {
      this.deactivate()
    }
  }

  private show(): void {
    if (!this.$el.attr('class')) {
      const $template = $(
        this.template({
          advertisement_starting_in: this.core.i18n.t(
            'advertisement_starting_in'
          )
        })
      )

      this.$el.html($template.html()).attr('class', $template.attr('class'))
      this.mediaControl?.$el.append(this.el)
    }

    this.$el.show()
    this.mediaControl?.$el.addClass('eplayer-time-to-ad-visible')
  }

  private hide(): void {
    this.$el.hide()
    this.mediaControl?.$el.removeClass('eplayer-time-to-ad-visible')
  }

  private deactivate(): void {
    this.isActive = false
    this.hide()
  }

  public destroy(): void {
    this.deactivate()
    super.destroy()
  }
}
